/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
 
(function () {
  const selector = '.shopify-section--logo-list section.section .logo-list__list';
  const jquerySrc = 'https://code.jquery.com/jquery-3.7.1.min.js';
  const slickJsSrc = 'https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.min.js';
  const slickCssSrc = 'https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.css';

  function loadStyle(href) {
    if (document.querySelector('link[data-igk-slick-css]')) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-igk-slick-css', 'true');
    document.head.appendChild(link);
  }

  function loadScript(src, id) {
    return new Promise((resolve, reject) => {
      const existing = document.getElementById(id);

      if (existing) {
        if (existing.getAttribute('data-loaded') === 'true') {
          resolve();
          return;
        }

        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;
      script.addEventListener('load', () => {
        script.setAttribute('data-loaded', 'true');
        resolve();
      }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
  }

  function startCarousel() {
    if (!window.jQuery || !window.jQuery.fn || !window.jQuery.fn.slick) return;

    document.querySelectorAll(selector).forEach((element) => {
      if (element.classList.contains('slick-initialized')) return;

      window.jQuery(element).slick({
        autoplay: true,
        arrows: false,
        autoplaySpeed: 0,
        speed: 4000,
        infinite: true,
        slidesToShow: 9,
        cssEase: 'linear',
        pauseOnHover: true,
        pauseOnFocus: false,
        responsive: [
          {
            breakpoint: 768,
            settings: {
              slidesToShow: 6
            }
          },
          {
            breakpoint: 480,
            settings: {
              slidesToShow: 3
            }
          }
        ]
      });
    });
  }

  async function initCarousel() {
    if (!document.querySelector(selector)) return;

    loadStyle(slickCssSrc);

    try {
      if (!window.jQuery) {
        await loadScript(jquerySrc, 'igk-jquery');
      }

      if (!window.jQuery.fn || !window.jQuery.fn.slick) {
        await loadScript(slickJsSrc, 'igk-slick');
      }

      startCarousel();
    } catch (error) {
      console.warn('IGK logo carousel could not load.', error);
    }
  }

  function scheduleInit() {
    window.requestAnimationFrame(() => {
      initCarousel();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit, { once: true });
  } else {
    scheduleInit();
  }

  document.addEventListener('shopify:section:load', scheduleInit);
})();

/* IGK - Mobile Tap Guard - 2026-09-28
 * Prevent invisible/stale overlays from Focal or app UI from trapping taps on mobile.
 * Visual layout is untouched; this only controls pointer hit-testing for hidden layers.
 */
(function () {
  const isMobile = () => window.matchMedia('(max-width: 767px)').matches;
  const guardAttr = 'data-igk-tap-guard';
  const themeOverlaySelector = '.drawer__overlay, .modal__overlay, .popover__overlay, .openable__overlay';
  const themeHostSelector = 'cart-drawer, drawer-content, modal-content, popover-content, openable-element';
  let raf = 0;

  function restore(el) {
    if (!el || !el.hasAttribute(guardAttr)) return;
    el.style.removeProperty('pointer-events');
    el.removeAttribute(guardAttr);
  }

  function block(el, reason) {
    if (!el || el.getAttribute(guardAttr) === reason) return;
    el.style.setProperty('pointer-events', 'none', 'important');
    el.setAttribute(guardAttr, reason);
  }

  function syncThemeOverlays() {
    document.querySelectorAll(themeOverlaySelector).forEach((overlay) => {
      const host = overlay.closest(themeHostSelector);
      if (!host) return;

      if (host.hasAttribute('open')) {
        restore(overlay);
      } else {
        block(overlay, 'closed-theme-overlay');
      }
    });
  }

  function syncHiddenFixedLayers() {
    document.querySelectorAll('[aria-hidden="true"], [hidden]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const style = getComputedStyle(el);
      if (style.position !== 'fixed' || style.pointerEvents === 'none') return;

      const rect = el.getBoundingClientRect();
      const coversScreen = rect.width >= window.innerWidth * 0.7 && rect.height >= window.innerHeight * 0.7;
      if (coversScreen) block(el, 'hidden-fixed-layer');
    });

    document.querySelectorAll('[' + guardAttr + '="hidden-fixed-layer"]').forEach((el) => {
      const style = getComputedStyle(el);
      const stillHidden = el.hidden || el.getAttribute('aria-hidden') === 'true' || style.visibility === 'hidden' || Number(style.opacity) === 0;
      if (!stillHidden) restore(el);
    });
  }

  function sync() {
    if (!isMobile()) return;
    syncThemeOverlays();
    syncHiddenFixedLayers();
  }

  function scheduleSync() {
    if (!isMobile() || raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      sync();
    });
  }

  document.addEventListener('DOMContentLoaded', sync, { once: true });
  window.addEventListener('pageshow', () => setTimeout(sync, 50));
  window.addEventListener('orientationchange', () => setTimeout(sync, 250));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) setTimeout(sync, 50);
  });

  const observer = new MutationObserver(scheduleSync);
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['open', 'aria-hidden', 'hidden', 'class', 'style']
  });

  setTimeout(sync, 400);
  setTimeout(sync, 1500);
})();
